import { AccredibleSelectInputOption } from '@accredible-frontend-v2/forms/select-input';
import { DefaultFilters } from '@accredible-frontend-v2/models';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounceTime, filter, take, withLatestFrom } from 'rxjs/operators';
import {
  SpotlightProfilesFilters,
  SpotlightProfilesOrder,
} from '../../../../core/models/spotlight-profiles.model';
import { DirectoryState } from '../../../../core/stores/directory/directory.reducer';
import { selectDirectoryAvailableSearchByFilters } from '../../../../core/stores/directory/directory.selectors';
import { FilterService } from '../../services/filter.service';
import {
  SpotlightProfilesState,
  SpotlightProfilesStateAction,
} from '../../store/spotlight-profiles/spotlight-profiles.reducer';
import {
  selectSpotlightProfilesFilters,
  selectSpotlightProfilesStateAction,
} from '../../store/spotlight-profiles/spotlight-profiles.selectors';

enum SortTranslations {
  DEFAULT = 'profile-list-search-sort.default',
  DEFAULT_NEWEST = 'profile-list-search-sort.default_newest',
  MOST_COMPLETED = 'profile-list-search-sort.most_completed',
  DISTANCE = 'profile-list-search-sort.distance',
  NEWEST_FIRST = 'profile-list-search-sort.newest_first',
  OLDEST_FIRST = 'profile-list-search-sort.oldest_first',
  NAME_A_Z = 'profile-list-search-sort.name_a_z',
  NAME_Z_A = 'profile-list-search-sort.name_z_a',
}

@Component({
  selector: 'sl-profile-list-search-sort',
  templateUrl: './profile-list-search-sort.component.html',
  styleUrls: ['./profile-list-search-sort.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileListSearchSortComponent implements OnInit {
  private readonly _spotlightProfileStore = inject(Store<SpotlightProfilesState>);
  private readonly _directoryStore = inject(Store<DirectoryState>);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _filter = inject(FilterService);
  private readonly _destroyRef = inject(DestroyRef);

  directoryFilters = input<DefaultFilters>();
  showFiltersButton = input(false);
  isInitialLoad = input(true);

  showFilters = output<boolean>();

  searchByFilters$ = this._directoryStore.select(selectDirectoryAvailableSearchByFilters);

  defaultSortOption = SpotlightProfilesOrder.DEFAULT_NEWEST;

  sortFormControl = new FormControl<SpotlightProfilesOrder>(SpotlightProfilesOrder.DEFAULT_NEWEST);

  sortOptions = computed(() => {
    return [
      {
        name: this._language.translate(SortTranslations.DEFAULT_NEWEST),
        value: SpotlightProfilesOrder.DEFAULT_NEWEST,
      },
      {
        name: this._language.translate(SortTranslations.MOST_COMPLETED),
        value: SpotlightProfilesOrder.MOST_COMPLETED,
      },
      {
        name: this._language.translate(SortTranslations.NEWEST_FIRST),
        value: SpotlightProfilesOrder.NEWEST_FIRST,
      },
      {
        name: this._language.translate(SortTranslations.OLDEST_FIRST),
        value: SpotlightProfilesOrder.OLDEST_FIRST,
      },
      {
        name: this._language.translate(SortTranslations.NAME_A_Z),
        value: SpotlightProfilesOrder.NAME_A_Z,
      },
      {
        name: this._language.translate(SortTranslations.NAME_Z_A),
        value: SpotlightProfilesOrder.NAME_Z_A,
      },
      {
        name: this._language.translate(SortTranslations.DISTANCE),
        value: SpotlightProfilesOrder.DISTANCE,
        disabled: true,
        disabledReason: this._language.translate('profile-list-search-sort.tooltip'),
      },
    ];
  });

  ngOnInit(): void {
    this._setupSortFormControlValueChanges();
    this._setupFiltersListener();
    this._setupFiltersResetListener();
  }

  openMobileFiltersMenu(): void {
    this.showFilters.emit(true);
  }

  onChangeSearchCriteria(search: string): void {
    // Update url query params - remove name query param if search is empty
    const queryParamKey = 'search_term';
    const queryParamValue = search.length > 0 ? search : null;

    this._filter.updateRouterQueryParams({
      [queryParamKey]: queryParamValue,
    });
  }

  private _setupSortFormControlValueChanges(): void {
    this.sortFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        withLatestFrom(this._spotlightProfileStore.select(selectSpotlightProfilesFilters)),
        filter(([sorting, currentFilters]) => sorting !== currentFilters.order),
      )
      .subscribe(([sorting]) => {
        this._onChangeSortCriteria(<SpotlightProfilesOrder>sorting);
      });
  }

  private _setupFiltersListener(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesFilters)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((filters) => !!filters),
      )
      .subscribe((filters) => {
        const cityFilterActive = !!filters.city;

        // If we have an order value update the form
        this.sortFormControl.setValue(filters.order || this.defaultSortOption, {
          emitEvent: false,
        });
      });
  }

  private _setupFiltersResetListener(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesStateAction)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((action) => action === SpotlightProfilesStateAction.FILTERS_RESET),
      )
      .subscribe(() => {
        this._resetInputs();
      });
  }

  private _getLocationOption(): AccredibleSelectInputOption {
    return this.sortOptions().find((option) => option.value === SpotlightProfilesOrder.DISTANCE);
  }

  private _resetInputs(): void {
    this.sortFormControl.reset(this.defaultSortOption, {
      emitEvent: false,
    });
  }

  private _onChangeSortCriteria(sorting: SpotlightProfilesOrder): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesFilters)
      .pipe(take(1))
      .subscribe((currentFilters) => {
        this._updateSortQueryParam(sorting, currentFilters);
      });
  }

  private _updateSortQueryParam(
    sorting: SpotlightProfilesOrder,
    currentFilters: SpotlightProfilesFilters,
  ): void {
    const cityFilterActive = !!currentFilters.city;

    if (this.sortFormControl.value === SpotlightProfilesOrder.DISTANCE && !cityFilterActive) {
      sorting = this.defaultSortOption;
    }

    // Update url query params if sorting is set to default we remove the order query param
    this._filter.updateRouterQueryParams({
      order: sorting === this.defaultSortOption ? null : sorting,
    });
  }
}
